import React, { useRef, useState } from 'react';
import {
  ImagesSidebarPanel,
  ImagesSidebarContent,
  ImagesSidebarHeader,
  ImagesSidebarTitle,
  ImagesSidebarDescription,
  SlotCardWrapper,
  SlotCard,
  SlotBadge,
  SlotBody,
  SlotTitle,
  SlotSubtitle,
  SlotThumb,
  SlotThumbEmpty,
  SlotMenuButton,
  SlotMenu,
  SlotMenuItem,
  SlotMenuBackdrop,
  AddMoreButton,
  AddMoreLabel,
  AddMoreHelper,
  SidebarTipsCard,
  SidebarTipsIcon,
  SidebarTipsBody,
  SidebarTipsTitle,
  SidebarTipsText,
  HiddenFileInput,
} from '../imageCanvasStyles';

const ACCEPTED_TYPES = [ 'image/png', 'image/jpeg', 'image/jpg', 'image/webp' ];
const MAX_SLOTS = 4;

const SLOTS = [
  { title: 'Front image', subtitle: 'Main view' },
  { title: 'Back image', subtitle: 'Optional' },
  { title: 'Left image', subtitle: 'Optional' },
  { title: 'Right image', subtitle: 'Optional' },
];

const ImagesSidebar = ({
  theme,
  selectedTheme,
  images,
  activeId,
  onSelect,
  onRemove,
  onFiles,
  onReplace,
}) => {
  const inputRef = useRef(null);
  const replaceTargetRef = useRef(null);
  const [ openMenuId, setOpenMenuId ] = useState(null);
  const shared = { theme, $selectedTheme: selectedTheme };
  const canAddMore = images.length < MAX_SLOTS;

  const openPicker = (replaceId = null) => {
    replaceTargetRef.current = replaceId;
    inputRef.current?.click();
  };

  const handleInputChange = e => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    const replaceId = replaceTargetRef.current;
    replaceTargetRef.current = null;
    if (replaceId) onReplace(replaceId, files[0]);
    else onFiles(files);
  };

  const handleCardClick = img => {
    if (img) onSelect(img.id);
    else openPicker();
  };

  const handleMenuToggle = (e, id) => {
    e.stopPropagation();
    setOpenMenuId(prev => (prev === id ? null : id));
  };

  const handleReplaceClick = id => {
    setOpenMenuId(null);
    openPicker(id);
  };

  const handleRemoveClick = id => {
    setOpenMenuId(null);
    onRemove(id);
  };

  return (
    <ImagesSidebarPanel {...shared} aria-label='Images and angles'>
      <ImagesSidebarContent>
        <ImagesSidebarHeader>
          <ImagesSidebarTitle {...shared}>IMAGES &amp; ANGLES</ImagesSidebarTitle>
          <ImagesSidebarDescription {...shared}>
            Upload up to 4 images to help AI understand your model better.
          </ImagesSidebarDescription>
        </ImagesSidebarHeader>

        {SLOTS.map((slot, index) => {
          const { title, subtitle } = slot;
          const img = images[index] || null;
          const selected = Boolean(img) && img.id === activeId;

          return (
            <SlotCardWrapper key={title}>
              <SlotCard
                {...shared}
                type='button'
                $selected={selected}
                onClick={() => handleCardClick(img)}
                aria-pressed={selected}
                aria-label={img ? `Select ${title}` : `Upload ${title}`}
              >
                <SlotBadge {...shared} $selected={selected}>{index + 1}</SlotBadge>
                <SlotBody>
                  <SlotTitle {...shared}>{title}</SlotTitle>
                  <SlotSubtitle {...shared}>{subtitle}</SlotSubtitle>
                </SlotBody>
                {img
                  ? <SlotThumb {...shared} src={img.current} alt={img.name} />
                  : <SlotThumbEmpty {...shared} aria-hidden='true'>+</SlotThumbEmpty>}
              </SlotCard>

              {img && (
                <SlotMenuButton
                  {...shared}
                  role='button'
                  tabIndex={0}
                  aria-label={`Actions for ${title}`}
                  onClick={e => handleMenuToggle(e, img.id)}
                >
                  &#8942;
                </SlotMenuButton>
              )}

              {img && openMenuId === img.id && (
                <SlotMenu {...shared}>
                  <SlotMenuItem {...shared} type='button' onClick={() => handleReplaceClick(img.id)}>
                    Replace image
                  </SlotMenuItem>
                  <SlotMenuItem {...shared} type='button' $danger onClick={() => handleRemoveClick(img.id)}>
                    Remove image
                  </SlotMenuItem>
                </SlotMenu>
              )}
            </SlotCardWrapper>
          );
        })}

        <AddMoreButton
          {...shared}
          type='button'
          disabled={!canAddMore}
          onClick={() => openPicker()}
        >
          <AddMoreLabel>+ Add more images</AddMoreLabel>
          <AddMoreHelper {...shared}>(Max {MAX_SLOTS} images)</AddMoreHelper>
        </AddMoreButton>

        <SidebarTipsCard {...shared}>
          <SidebarTipsIcon aria-hidden='true'>&#128161;</SidebarTipsIcon>
          <SidebarTipsBody>
            <SidebarTipsTitle {...shared}>Tips</SidebarTipsTitle>
            <SidebarTipsText {...shared}>
              Good quality images from all sides help generate more accurate 3D models.
            </SidebarTipsText>
          </SidebarTipsBody>
        </SidebarTipsCard>

        <HiddenFileInput
          ref={inputRef}
          type='file'
          multiple
          accept={ACCEPTED_TYPES.join(',')}
          onChange={handleInputChange}
        />
      </ImagesSidebarContent>

      {openMenuId && <SlotMenuBackdrop onClick={() => setOpenMenuId(null)} />}
    </ImagesSidebarPanel>
  );
};

export { ImagesSidebar };
